import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthProtection } from '../hooks/useAuthProtection';
import { useCandidateProfile } from '../hooks/useCandidateProfile';
import { candidateServiceApi } from '../services/candidateServiceApi';
import CandidateLayout from '../components/CandidateLayout';
import StatsCards from '../components/myprofile/StatsCards';
import ProfileSummaryCard from '../components/myprofile/ProfileSummaryCard';
import WorkExperienceSection from '../components/myprofile/WorkExperienceSection';
import EducationSection from '../components/myprofile/EducationSection';
import CareerPreferencesSection from '../components/myprofile/CareerPreferencesSection';
import ProfessionalLinksCard from '../components/myprofile/ProfessionalLinksCard';
import CertificationsSection from '../components/myprofile/CertificationsSection';
import AboutCard from '../components/myprofile/AboutCard';

/**
 * My Profile Page - Main candidate profile overview
 * Matches Figma design with stats, profile strength, experience, education
 * Route: /candidate/my-profile
 */
const MyProfile: React.FC = () => {
    useAuthProtection();
    const navigate = useNavigate();
    const { profile, loading, error, refetch } = useCandidateProfile();
    const [userName, setUserName] = useState('');
    const [userEmail, setUserEmail] = useState('');

    // Get user info from localStorage
    useEffect(() => {
        const userDataString = localStorage.getItem('authUser');
        if (userDataString) {
            try {
                const userData = JSON.parse(userDataString);
                setUserName(`${userData.firstName || ''} ${userData.lastName || ''}`.trim());
                setUserEmail(userData.email || '');
            } catch (error) {
                console.error('Failed to parse user data:', error);
            }
        }
    }, []);

    // Handle career preferences save
    // NOTE: Per Redesigned_embedding_matching_strategy.md, this triggers Preferences embedding regeneration
    const handleSaveCareerPreferences = useCallback(async (preferences: {
        locations: string[];
        minCompensation: string;
        maxCompensation: string;
        workType: string[];
        jobTitles: string[];
        startDateAvailability: string;
        lookingFor: string;
    }) => {
        await candidateServiceApi.updateProfileMe({
            preferences: {
                cities: preferences.locations,
                minSalary: preferences.minCompensation ? parseInt(preferences.minCompensation) : undefined,
                maxSalary: preferences.maxCompensation ? parseInt(preferences.maxCompensation) : undefined,
                workType: preferences.workType.join(', '),
                jobTitles: preferences.jobTitles,
                startDate: preferences.startDateAvailability,
                lookingFor: preferences.lookingFor,
            }
        });

        // Refetch profile to update the UI
        if (refetch) {
            await refetch();
        }
    }, [refetch]);

    // Handle work experience save
    // NOTE: Per Redesigned_embedding_matching_strategy.md, this triggers Resume/Skills/Summary embedding regeneration
    const handleSaveWorkExperience = useCallback(async (experiences: {
        title: string;
        company: string;
        startDate: string;
        endDate: string;
        isCurrent?: boolean;
        description?: string;
    }[]) => {
        await candidateServiceApi.updateProfileMe({ experience: experiences });
        if (refetch) await refetch();
    }, [refetch]);

    // Handle education save
    // NOTE: Per Redesigned_embedding_matching_strategy.md, this triggers Resume embedding regeneration
    const handleSaveEducation = useCallback(async (education: {
        degree: string;
        institution: string;
        fieldOfStudy?: string;
        startDate: string;
        endDate: string;
    }[]) => {
        await candidateServiceApi.updateProfileMe({ education: education });
        if (refetch) await refetch();
    }, [refetch]);

    // Handle summary/about save
    // NOTE: Per Redesigned_embedding_matching_strategy.md, this triggers Summary embedding regeneration
    const handleSaveSummary = useCallback(async (summary: string) => {
        await candidateServiceApi.updateProfileMe({ summary });
        if (refetch) await refetch();
    }, [refetch]);

    // Handle certifications save
    const handleSaveCertifications = useCallback(async (certifications: {
        name: string;
        issuer: string;
        issueDate: string;
        expirationDate: string;
        credentialId: string;
        url: string;
    }[]) => {
        await candidateServiceApi.updateProfileMe({ certifications });
        if (refetch) await refetch();
    }, [refetch]);

    // Handle professional links save
    // NOTE: Per Redesigned_embedding_matching_strategy.md, this does NOT trigger any embedding regeneration
    const handleSaveProfessionalLinks = useCallback(async (links: {
        linkedin?: string;
        twitter?: string;
        portfolio?: string;
        others?: Array<{ name: string; url: string }>;
    }) => {
        await candidateServiceApi.updateProfileMe({ professionalLinks: links });
        if (refetch) await refetch();
    }, [refetch]);

    // Loading state
    if (loading) {
        return (
            <CandidateLayout
                headerTitle="My Profile"
                headerSubtitle="Here's what's happening in your professional space."
            >
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#015FA6] mx-auto mb-4"></div>
                        <p className="text-[#515151]">Loading profile...</p>
                    </div>
                </div>
            </CandidateLayout>
        );
    }

    // Error state
    if (error || !profile) {
        return (
            <CandidateLayout
                headerTitle="My Profile"
                headerSubtitle="Here's what's happening in your professional space."
            >
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-2xl mx-auto">
                    <h2 className="text-xl font-semibold text-yellow-600 mb-2">Profile Not Found</h2>
                    <p className="text-yellow-700">
                        {error || 'No profile data found. Please upload your resume to create a profile.'}
                    </p>
                    <button
                        onClick={() => navigate('/candidate/dashboard?view=resume-upload')}
                        className="mt-4 px-4 py-2 bg-[#015FA6] text-white rounded-lg hover:bg-[#014a85]"
                    >
                        Upload Resume
                    </button>
                </div>
            </CandidateLayout>
        );
    }

    return (
        <CandidateLayout
            headerTitle="My Profile"
            headerSubtitle="Here's what's happening in your professional space."
        >
            {/* Content Area */}
            <div className="space-y-6">
                    {/* Stats Cards Row */}
                    <StatsCards profile={profile} />

                    {/* Two Column Layout */}
                    <div className="flex flex-col lg:flex-row gap-6">
                        {/* Left Section: Profile Summary + About + Career Preferences */}
                        <div className="flex flex-col gap-6 w-full lg:w-1/2">
                            {/* Profile Summary */}
                            <ProfileSummaryCard
                                profile={profile}
                                userName={userName}
                                userEmail={userEmail}
                            />

                            {/* About/Summary Card */}
                            <AboutCard
                                profile={profile}
                                onSave={handleSaveSummary}
                            />

                            {/* Career Preferences (moved to top per meeting) */}
                            <CareerPreferencesSection
                                profile={profile}
                                onSave={handleSaveCareerPreferences}
                            />

                            {/* Education (under Career Preferences per meeting) */}
                            <EducationSection profile={profile} onSave={handleSaveEducation} />

                            {/* Licenses & Certifications */}
                            <CertificationsSection profile={profile} onSave={handleSaveCertifications} />
                        </div>

                        {/* Right Section: Work Experience + Links */}
                        <div className="flex flex-col gap-6 w-full lg:w-1/2">
                            {/* Work Experience */}
                            <WorkExperienceSection profile={profile} onSave={handleSaveWorkExperience} />

                            {/* Professional Links Card */}
                            <ProfessionalLinksCard
                                profile={profile}
                                onSave={handleSaveProfessionalLinks}
                            />
                        </div>
                    </div>
            </div>
        </CandidateLayout>
    );
};

export default MyProfile;
